import React, {useEffect, useState} from 'react';
import {getUserData} from "../../services/API";
import ReactTooltip from 'react-tooltip';
import moment from 'moment';
import { BADGE_LEVELS, BADGE_TYPE } from "../../services/badging/constants";
import {sortBadgesByLevelDesc, getBadgeImage} from "./utils";
import BadgeGrid from "./BadgeGrid";
import { COPY_STRINGS } from '../../constants/translatedCopy';
import read_a_story from '../../images/voice_picker_icons/read_a_story.svg';
import read_a_story_spanish from '../../images/voice_picker_icons/read_a_story_spanish.svg';
import doNotUseAccessibleStaticElementInteractions from "A11y/doNotUseAccessibleStaticElementInteractions.js";
import backIcon from '../../images/icons/back.svg';
import { getCopyByGradeband } from '../../services/ThemingService';


export default function BadgingProgress(props) {
  const { badges, firstName, locale } = getUserData();
  const {
    wcpm, accuracy, numberOfStories, getProgressTextToSpeak, fromStudentInbox,
    badgeSectionHighlighted, nextBadgeHighlighted, badgeLevelHighlighted, setLocale, goHome,
  } = props;
  const [highestBadge, setHighestBadge] = useState({});
  const [nextBadge, setNextBadge] = useState(null);
  const filteredBadges = badges.filter((badge) => badge.awarded);
  const [patterFinished, setPatterFinished] = useState(false);

  const formatHighestBadgeText = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const getNextHighestBadge = (badge) => {
    if (highestBadge.rewardLevelId === BADGE_LEVELS[BADGE_LEVELS.length - 1]) return null;
    const nextBadgeLevelIndex = BADGE_LEVELS.findIndex((x) => x === badge.rewardLevelId) + 1;
    const nextBadge = BADGE_LEVELS[nextBadgeLevelIndex];
    return nextBadge;
  };

  useEffect(() => {
    const highestBadge = sortBadgesByLevelDesc(filteredBadges)[0];
    const timeout = setTimeout(()=> {
      if (fromStudentInbox) {
        props.goHome();
        return;
      }
      if (getUserData().tutorEnabled && !getUserData().nonverbal) {
        props.goToReadStory();
      } else {
        props.goHome();
      }
    }, 60000);
    if (highestBadge){
      setHighestBadge(highestBadge);
    }
    return () => {
      clearTimeout(timeout);
    }
  }, []);

  useEffect(() => {
    setNextBadge(getNextHighestBadge(highestBadge));
  }, [highestBadge]);

  useEffect(()=> {
    if (!numberOfStories && numberOfStories !== 0) return;
    const onEnd = () => setPatterFinished(true);
    getProgressTextToSpeak({numberOfStories, badgingEnabled: true, badges:filteredBadges, nextBadge, highestBadge, onEnd});
  }, [numberOfStories])

  const badgeSectionClass = badgeSectionHighlighted ? 'badgeSectionHighlighted badgeSection' : 'badgeSection';
  const nextBadgeClass = nextBadgeHighlighted ? 'nextBadgeHighlighted nextBadge' : 'nextBadge';
  const badgeLevelClass = badgeLevelHighlighted ? 'badgeModalTitleHighlighted badgeModalTitle' : 'badgeModalTitle';
  const optionTitleClass = getUserData().isESL ? 'option-title esl' : 'option-title';

  const onReadAStoryClick = locale => {
    setLocale(locale);
    props.goToReadStory();
  }

  return (
    <div className="badgingProgressContainer">
      {nextBadge && (
        <div className={nextBadgeClass}>
          {COPY_STRINGS.badging_next_be}:
          <img className="nextBadgeImage" src={getBadgeImage(locale, nextBadge)} alt={`${nextBadge} badge`}  />
        </div>
      )}
      {patterFinished && (getUserData().tutorEnabled || fromStudentInbox) &&
        <div className="read-a-story-wrapper">
          <div className="options-wrapper">
            { fromStudentInbox || getUserData().nonverbal ? (
              <div className="optionButton"
                {...doNotUseAccessibleStaticElementInteractions({onClick: goHome})}>
                <img src={backIcon} alt=""/>
                <span className={optionTitleClass}>Back to Menu</span>
              </div>
            ): (
              <>
                <div className="optionButton"
                  {...doNotUseAccessibleStaticElementInteractions({onClick: () => onReadAStoryClick('en-us')})}>
                  <img src={read_a_story} alt=""/>
                  <span className={optionTitleClass}>{getCopyByGradeband(COPY_STRINGS, 'read_a_story')}</span>
                </div>
                {getUserData().isESL && 
                  (<div className="optionButton"
                    {...doNotUseAccessibleStaticElementInteractions({onClick: () => onReadAStoryClick('es-mx')})}>
                    <img src={read_a_story_spanish} alt=""/>
                    <span className={optionTitleClass}>{getCopyByGradeband(COPY_STRINGS, 'read_a_story_in_spanish')}</span>
                  </div>)
                }
              </>
            )}
          </div>
        </div>
      }
      <BadgeGrid badges={badges} />
      <div className="endOfStoryModal badging">
        <ReactTooltip
          id="badgingFlyover"
          effect="solid"
          clickable={true}
          place="right"
          getContent={(dataTip) => {
            if (!dataTip) return <div></div>;
            const { rewardLevelId, locale, badgeTypeId, awardedAt } = JSON.parse(dataTip);
            const awardLevel = BADGE_LEVELS.findIndex((x) => x === rewardLevelId) + 1;
            return (
              <div className="badging-flyover">
                <div className="badging-flyover-title">{COPY_STRINGS.BADGE_TITLE[badgeTypeId][rewardLevelId]}</div>
                <img className="badging-flyover-img" src={getBadgeImage(locale, rewardLevelId)} alt={`${rewardLevelId} badge`} />
                <span className="badge-flyover-award-date">{moment(awardedAt).format('MMM DD, YYYY')}</span>
                <span className="badge-flyover-award-level">{COPY_STRINGS.badging_level} #{awardLevel}</span>
              </div>
            );
          }}
          //this prevents the tooltip from being given an inline position so that we can apply styling
          //with the stylesheet without using !important
          overridePosition={() => ({})}
        />
        <div className="badgeStatSection">
          <div className="badgeSectionTitle">{COPY_STRINGS.badging_your_reading}:</div>
          <div className="badgeSectionContainer">
            <div className={badgeSectionClass}>
              <div className="badgingStats">
                <div className="badgingStatContainer">
                  <div className="badgingStat">{COPY_STRINGS.badging_speed}:</div>
                  {wcpm && <div className="badgingStat">{wcpm} {COPY_STRINGS.badging_words}</div>}
                </div>
              </div>
            </div>
            <div className={badgeSectionClass}>
              <div className="badgingStats">
                <div className="badgingStatContainer">
                  <div className="badgingStat">{COPY_STRINGS.badging_correct}: </div>
                  {accuracy && <div className="badgingStat">{accuracy + '%'}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {highestBadge.rewardLevelId && (
          <div className={badgeLevelClass}>
            {`${firstName}, `}
            {COPY_STRINGS.you_are_badge_level({ rewardLevelId: formatHighestBadgeText(highestBadge.rewardLevelId) })}
          </div>
        )}
      </div>
    </div>
  );
}
